import {
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  recordAudio,
  stopRecording,
  pauseRecording,
  resumeRecording,
  resetAudioChunks,
} from '../components/recordAudio';
import {
  useSpeechToText,
} from '../components/speechToText';
import {
  claimTTSPlayer,
  isPersistentTTSPlayer,
  playChunkedTTS,
  primeTTSPlayer,
  releaseTTSPlayer,
  textToSpeech,
} from '../components/textToSpeech';
import { Message } from '../models/Message';
import {
  CourseActivityAttempt,
  CourseActivityMessage,
  editCourseActivityLastMessage,
  sendCourseActivityMessage,
  suggestCourseActivityReply,
} from '../services/courseActivity';

const IOS_SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAVFQAAKhYAQACABAAZGF0YQAAAAA=';

const wait = (milliseconds: number) =>
  new Promise<void>(resolve => {
    window.setTimeout(resolve, milliseconds);
  });

interface UseCourseActivitySessionOptions {
  attempt: CourseActivityAttempt;
  initialMessages: CourseActivityMessage[];
  onAttemptChange?: (
    attempt: CourseActivityAttempt
  ) => void;
}

export function useCourseActivitySession({
  attempt,
  initialMessages,
  onAttemptChange,
}: UseCourseActivitySessionOptions) {
  const { transcribe } = useSpeechToText();

  const [messages, setMessages] =
    useState<Message[]>(initialMessages);

  const [input, setInput] = useState('');
  const [showSendIcon, setShowSendIcon] =
    useState(false);

  const [transcription, setTranscription] =
    useState<string | null>(null);

  const [isMicCanceled, setIsMicCanceled] =
    useState(false);

  const [isPauseStarted, setIsPauseStarted] =
    useState(false);

  const [isAudioPaused, setIsAudioPaused] =
    useState(false);

  const [errorAppeared, setErrorAppeared] =
    useState(false);

  const [audioBlob, setAudioBlob] =
    useState<Blob | null>(null);

  const [audioURL, setAudioURL] =
    useState<string | null>(null);

  const [isMicActivated, setIsMicActivated] =
    useState(false);

  const [
    isStopRecordingAvailable,
    setIsStopRecordingAvailable,
  ] = useState(false);

  const [audioConfirmed, setAudioConfirmed] =
    useState(false);

  const [isBotSpeaking, setIsBotSpeaking] =
    useState(false);

  const [playingIndex, setPlayingIndex] =
    useState<number | null>(null);

  const [
    pausedPlaybackIndex,
    setPausedPlaybackIndex,
  ] = useState<number | null>(null);

  const [isWaitingForBot, setIsWaitingForBot] =
    useState(false);

  const messagesEndRef =
    useRef<HTMLDivElement | null>(null);

  const playbackRef =
    useRef<HTMLAudioElement | null>(null);

  const manualAudioRef =
    useRef<HTMLAudioElement | null>(null);

  const botAudioCacheRef =
    useRef<Map<number, string>>(new Map());

  const userAudioCacheRef =
    useRef<Map<number, string>>(new Map());

  const [
    userAudioIndices,
    setUserAudioIndices,
  ] = useState<Set<number>>(new Set());

  const cancelChunkedTTSRef =
    useRef<(() => void) | null>(null);

  const ttsResolveRef =
    useRef<(() => void) | null>(null);

  const keepMicActiveRef = useRef(false);

  const stopCurrentPlayback = () => {
    const audio = playbackRef.current;

    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      audio.onended = null;
      audio.onerror = null;

      if (isPersistentTTSPlayer(audio)) {
        releaseTTSPlayer();
      }

      playbackRef.current = null;
    }

    cancelChunkedTTSRef.current?.();
    cancelChunkedTTSRef.current = null;

    setPlayingIndex(null);
    setPausedPlaybackIndex(null);
  };

  const pauseCurrentPlayback = () => {
    const audio = playbackRef.current;

    if (!audio || playingIndex === null) {
      return;
    }

    audio.pause();
    setPausedPlaybackIndex(playingIndex);
    setPlayingIndex(null);
  };

  const resumeCurrentPlayback = () => {
    const audio = playbackRef.current;

    if (!audio || pausedPlaybackIndex === null) {
      return;
    }

    const index = pausedPlaybackIndex;

    setPausedPlaybackIndex(null);
    setPlayingIndex(index);

    void audio.play().catch(() => {
      playbackRef.current = null;
      setPlayingIndex(null);
      setPausedPlaybackIndex(null);
    });
  };

  const stopBotSpeaking = () => {
    cancelChunkedTTSRef.current?.();
    cancelChunkedTTSRef.current = null;

    ttsResolveRef.current?.();
    ttsResolveRef.current = null;

    setIsBotSpeaking(false);
  };

  const cancelMicFlow = () => {
    stopBotSpeaking();

    setIsMicActivated(false);
    setIsStopRecordingAvailable(false);
    setIsMicCanceled(true);
  };

  const resumeAudioPaused = () => {
    setIsAudioPaused(false);
    resumeRecording();
  };

  const pauseAudio = async () => {
    setIsPauseStarted(true);
    setIsAudioPaused(true);

    await pauseRecording();
  };

  const cancelAudioFlow = async () => {
    if (isPauseStarted) {
      setIsPauseStarted(false);
      setIsAudioPaused(false);
      resetAudioChunks();
    }

    setIsMicActivated(false);
    setIsStopRecordingAvailable(false);

    await stopRecording();
    setAudioBlob(null);
    setTranscription(null);
  };

  const confirmSendAudio = async () => {
    primeTTSPlayer();
    setAudioConfirmed(true);

    if (isPauseStarted) {
      await pauseRecording();

      setIsAudioPaused(false);
      setIsPauseStarted(false);
    }

    const blob = await stopRecording();

    setAudioBlob(blob);
    setIsStopRecordingAvailable(false);

    if (isPauseStarted) {
      resetAudioChunks();
    }
  };

  const handleChange = (
    event:
      React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement
      >
  ) => {
    setInput(event.target.value);
    setShowSendIcon(
      event.target.value.trim().length > 0
    );
  };

  const activateMic = () => {
    if (isWaitingForBot) {
      return;
    }

    setErrorAppeared(false);

    if (isBotSpeaking) {
      keepMicActiveRef.current = true;
      stopBotSpeaking();
      setIsMicActivated(true);
      setIsMicCanceled(false);
      void startMicRecording();
      return;
    }

    if (
      playingIndex !== null ||
      pausedPlaybackIndex !== null
    ) {
      stopCurrentPlayback();
    }

    if (isMicActivated) {
      setIsMicActivated(false);
      setIsStopRecordingAvailable(false);
      setIsMicCanceled(true);
      return;
    }

    // Start the recorder synchronously from the tap/click path. iOS Safari is
    // stricter than Chrome about microphone activation after React state/effects.
    setIsMicActivated(true);
    setIsMicCanceled(false);
    void startMicRecording();
  };

  const playBotMessageAudio = async (
    index: number,
    content: string
  ) => {
    stopCurrentPlayback();

    const cachedUrl =
      botAudioCacheRef.current.get(index);

    // Cached TTS can start synchronously from this click/tap. This is the most
    // reliable path on iOS Safari and avoids losing user activation.
    if (cachedUrl) {
      const audio = claimTTSPlayer();
      playbackRef.current = audio;
      setPlayingIndex(index);
      setPausedPlaybackIndex(null);

      const onEnd = () => {
        if (playbackRef.current === audio) {
          playbackRef.current = null;
        }
        audio.onended = null;
        audio.onerror = null;
        releaseTTSPlayer();
        setPlayingIndex(null);
        setPausedPlaybackIndex(null);
      };

      audio.pause();
      audio.currentTime = 0;
      audio.src = cachedUrl;
      audio.onended = onEnd;
      audio.onerror = onEnd;

      void audio.play().catch(onEnd);
      return;
    }

    // For uncached TTS, activate the persistent Safari-safe player NOW, while
    // we are still inside the user's tap. The exact same Audio element is then
    // reused after the asynchronous /speech.php request resolves.
    primeTTSPlayer();
    const audio = claimTTSPlayer();
    playbackRef.current = audio;
    setPlayingIndex(index);
    setPausedPlaybackIndex(null);

    const onEnd = () => {
      if (playbackRef.current === audio) {
        playbackRef.current = null;
      }
      audio.onended = null;
      audio.onerror = null;
      releaseTTSPlayer();
      setPlayingIndex(null);
      setPausedPlaybackIndex(null);
    };

    let blob = await textToSpeech(content);

    if (!blob && playbackRef.current === audio) {
      await wait(250);
      blob = await textToSpeech(content);
    }

    if (!blob || playbackRef.current !== audio) {
      onEnd();
      return;
    }

    const url = URL.createObjectURL(blob);
    botAudioCacheRef.current.set(index, url);

    audio.pause();
    audio.currentTime = 0;
    audio.src = url;
    audio.onended = onEnd;
    audio.onerror = onEnd;

    void audio.play().catch(onEnd);
  };

  const playUserMessageAudio = (
    index: number
  ) => {
    stopCurrentPlayback();

    const url =
      userAudioCacheRef.current.get(index);

    if (!url) {
      return;
    }

    const audio =
      manualAudioRef.current ?? new Audio();

    manualAudioRef.current = audio;
    playbackRef.current = audio;
    setPlayingIndex(index);
    setPausedPlaybackIndex(null);

    const onEnd = () => {
      if (playbackRef.current === audio) {
        playbackRef.current = null;
      }
      audio.onended = null;
      audio.onerror = null;
      setPlayingIndex(null);
      setPausedPlaybackIndex(null);
    };

    // The learner recording already has a local URL, so start it directly in
    // the tap handler. Do not insert an async priming promise in front of it.
    audio.pause();
    audio.currentTime = 0;
    audio.src = url;
    audio.onended = onEnd;
    audio.onerror = onEnd;

    void audio.play().catch(onEnd);
  };

  const evictBotAudioCache = (
    index: number
  ) => {
    if (
      playingIndex === index ||
      pausedPlaybackIndex === index
    ) {
      stopCurrentPlayback();
    }

    const url =
      botAudioCacheRef.current.get(index);

    if (url) {
      URL.revokeObjectURL(url);
    }

    botAudioCacheRef.current.delete(index);
  };

  const getUserAudioUrl = (
    index: number
  ): string | undefined =>
    userAudioCacheRef.current.get(index);

  const speakAssistantReply = async (
    content: string
  ) => {
    setIsBotSpeaking(true);

    await new Promise<void>((resolve) => {
      ttsResolveRef.current = resolve;

      cancelChunkedTTSRef.current =
        playChunkedTTS(
          content,
          () => {
            ttsResolveRef.current = null;
            resolve();
          }
        );
    });

    cancelChunkedTTSRef.current = null;
    ttsResolveRef.current = null;
    setIsBotSpeaking(false);
  };

  const handleSubmit = async (
    newInput: string
  ) => {
    const content = newInput.trim();

    if (!content || isWaitingForBot) {
      return;
    }

    primeTTSPlayer();

    setInput('');
    setShowSendIcon(false);
    setErrorAppeared(false);

    const userMessage: Message = {
      role: 'user',
      content,
    };

    setMessages(previous => [
      ...previous,
      userMessage,
    ]);

    setIsWaitingForBot(true);

    try {
      const result =
        await sendCourseActivityMessage(
          attempt.id,
          content
        );

      setMessages(result.messages);
      onAttemptChange?.(result.attempt);

      const assistant =
        result.messages[
          result.messages.length - 1
        ];

      if (
        assistant?.role === 'assistant' &&
        isMicActivated &&
        attempt.interaction_mode !== 'writing'
      ) {
        await speakAssistantReply(
          assistant.content
        );
      }

      if (isMicActivated) {
        if (!keepMicActiveRef.current) {
          setIsMicActivated(false);
          setIsStopRecordingAvailable(false);
        }

        keepMicActiveRef.current = false;
      }
    } catch (error) {
      console.error(
        'Error sending Course Activity message:',
        error
      );

      setMessages(previous => {
        const last = previous[
          previous.length - 1
        ];

        return last?.role === 'user' &&
          last.content === content
          ? previous.slice(0, -1)
          : previous;
      });

      setIsMicActivated(false);
      setIsStopRecordingAvailable(false);
      setErrorAppeared(true);
    } finally {
      setIsWaitingForBot(false);
    }
  };

  const sendInput = () => {
    if (isWaitingForBot) {
      return;
    }

    primeTTSPlayer();
    setAudioURL(null);

    void handleSubmit(input);
  };

  const editLastMessage = async (
    newContent: string
  ) => {
    const content = newContent.trim();

    if (!content || isWaitingForBot) {
      return;
    }

    setInput('');
    setShowSendIcon(false);
    setErrorAppeared(false);
    setIsWaitingForBot(true);

    try {
      const result =
        await editCourseActivityLastMessage(
          attempt.id,
          content
        );

      setMessages(result.messages);
      onAttemptChange?.(result.attempt);
    } catch (error) {
      console.error(
        'Error editing Course Activity message:',
        error
      );

      setErrorAppeared(true);
    } finally {
      setIsWaitingForBot(false);
    }
  };

  const fetchSuggestion = async (
    _prompt?: string
  ): Promise<string | null> => {
    if (isWaitingForBot) {
      return null;
    }

    for (let attemptNumber = 0; attemptNumber < 2; attemptNumber += 1) {
      try {
        const suggestion =
          await suggestCourseActivityReply(
            attempt.id
          );

        if (suggestion?.trim()) {
          return suggestion;
        }
      } catch (error) {
        console.warn(
          `Course Activity suggestion attempt ${attemptNumber + 1} failed.`,
          error
        );
      }

      if (attemptNumber === 0) {
        await wait(250);
      }
    }

    return null;
  };

  async function startMicRecording() {
    setIsStopRecordingAvailable(true);

    try {
      await recordAudio();
    } catch (error) {
      console.error(
        'Unable to start Course Activity microphone:',
        error
      );
      setIsStopRecordingAvailable(false);
      setIsMicActivated(false);
      setIsMicCanceled(true);
      setErrorAppeared(true);
    }
  }

  useEffect(() => {
    if (!audioBlob || !isMicActivated) {
      return;
    }

    let cancelled = false;

    const processAudio = async () => {
      try {
        let text = await transcribe(
          audioBlob,
          attempt.language
        );

        if (!text?.trim() && !cancelled) {
          await wait(250);
          text = await transcribe(
            audioBlob,
            attempt.language
          );
        }

        if (cancelled) {
          return;
        }

        if (!text?.trim()) {
          throw new Error(
            'Course Activity transcription returned no text.'
          );
        }

        setTranscription(text);
      } catch (error) {
        console.error(
          'Error transcribing Course Activity audio:',
          error
        );

        if (!cancelled) {
          setErrorAppeared(true);
          await cancelAudioFlow();
        }
      }
    };

    void processAudio();

    return () => {
      cancelled = true;
    };
    // Keep the same audio lifecycle used by RETZIA.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioBlob]);

  useEffect(() => {
    const handleKeyPress = (
      event: KeyboardEvent
    ) => {
      if (
        event.key === 'Enter' &&
        isStopRecordingAvailable &&
        !isMicCanceled
      ) {
        void confirmSendAudio();
      }

      if (event.key === ' ') {
        if (
          isStopRecordingAvailable &&
          !isMicCanceled &&
          !isAudioPaused
        ) {
          void pauseAudio();
        } else if (
          isStopRecordingAvailable &&
          !isMicCanceled &&
          isPauseStarted &&
          isAudioPaused
        ) {
          resumeAudioPaused();
        }
      }

      if (event.key === 'Escape') {
        if (
          isStopRecordingAvailable &&
          !isMicCanceled
        ) {
          void cancelAudioFlow();
        } else if (
          !isStopRecordingAvailable &&
          isMicActivated
        ) {
          cancelMicFlow();
        }
      }
    };

    window.addEventListener(
      'keydown',
      handleKeyPress
    );

    return () => {
      window.removeEventListener(
        'keydown',
        handleKeyPress
      );
    };
    // Mirrors the existing RETZIA keyboard behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isStopRecordingAvailable,
    isMicCanceled,
    isAudioPaused,
    isMicActivated,
    isPauseStarted,
  ]);

  useEffect(() => {
    if (
      !transcription ||
      !isMicActivated
    ) {
      return;
    }

    const userMessageIndex =
      messages.length;

    if (audioBlob) {
      const url =
        URL.createObjectURL(audioBlob);

      userAudioCacheRef.current.set(
        userMessageIndex,
        url
      );

      setUserAudioIndices(previous => {
        const next = new Set(previous);
        next.add(userMessageIndex);
        return next;
      });
    }

    const text = transcription;

    setTranscription(null);
    setAudioBlob(null);

    void handleSubmit(text);
    // Submit exactly once for each finished transcription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcription]);

  useEffect(() => {
    if (!isMicActivated) {
      setAudioConfirmed(false);
    }
  }, [isMicActivated]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
    });
  }, [messages]);

  useEffect(() => {
    const botAudioCache =
      botAudioCacheRef.current;

    const userAudioCache =
      userAudioCacheRef.current;

    return () => {
      botAudioCache.forEach(
        url => URL.revokeObjectURL(url)
      );

      userAudioCache.forEach(
        url => URL.revokeObjectURL(url)
      );

      stopCurrentPlayback();

      if (manualAudioRef.current) {
        manualAudioRef.current.pause();
        manualAudioRef.current.src = '';
        manualAudioRef.current = null;
      }
    };
    // Audio caches live for the component session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isRecording =
    isStopRecordingAvailable &&
    !isMicCanceled;

  const isWaiting =
    !isStopRecordingAvailable &&
    isMicActivated;

  return {
    messages,
    input,
    showSendIcon,
    isMicCanceled,
    isPauseStarted,
    isAudioPaused,
    errorAppeared,
    audioURL,
    isMicActivated,
    isStopRecordingAvailable,
    messagesEndRef,
    handleSubmit,
    handleChange,
    activateMic,
    sendInput,
    cancelMicFlow,
    confirmSendAudio,
    resumeAudioPaused,
    pauseAudio,
    cancelAudioFlow,
    editLastMessage,
    playBotMessageAudio,
    playUserMessageAudio,
    userAudioIndices,
    fetchSuggestion,
    evictBotAudioCache,
    getUserAudioUrl,
    stopCurrentPlayback,
    pauseCurrentPlayback,
    resumeCurrentPlayback,
    pausedPlaybackIndex,
    audioConfirmed,
    isRecording,
    isWaiting,
    isBotSpeaking,
    stopBotSpeaking,
    playingIndex,
    isWaitingForBot,
    isConversationReady: true,
  };
}